import apiClient from '@/shared/api/client';
import { ApiResponse } from '@/shared/api/types';

export interface SearchTermSet {
  id: string;
  locationId: string;
  version: number;
  status: 'active' | 'archived';
  queries: string[];
  createdAt: string;
  archivedAt: string | null;
}

export interface SearchTermVariant {
  query: string;
  impressions: number;
  clicks: number;
  avgPosition: number | null;
}

/** Every Search Console query that counts toward one saved term — the service plus only location,
 *  "near me" or generic-qualifier words — rolled up by the backend (searchTermMatching.ts). */
export interface SearchTermRollUp {
  term: string;
  impressions: number;
  clicks: number;
  ctr: number | null;
  avgPosition: number | null;
  /** Most impressions first. Empty means no Search Console data for this service yet. */
  variants: SearchTermVariant[];
}

/** The active set as the editors read it: one `performance` entry per query, same order. */
export interface SearchTermSetWithPerformance extends SearchTermSet {
  performance: SearchTermRollUp[];
}

/** Which location's search terms, and through which door. With `practiceId` the call goes through the
 *  agency routes (an agency user editing a practice they manage); without it, through the practice's
 *  own `/seo` routes. Both read and write the same SearchTermSet. */
export interface SearchTermsScope {
  locationId: string;
  practiceId?: string;
}

const agencyPath = (practiceId: string, locationId: string) =>
  `/agency/practices/${practiceId}/locations/${locationId}/search-terms`;

export const searchTermsApi = {
  get: async ({
    locationId,
    practiceId,
  }: SearchTermsScope): Promise<SearchTermSetWithPerformance | null> => {
    const res = practiceId
      ? await apiClient.get<ApiResponse<SearchTermSetWithPerformance | null>>(
          agencyPath(practiceId, locationId),
        )
      : await apiClient.get<ApiResponse<SearchTermSetWithPerformance | null>>('/seo/search-terms', {
          params: { locationId },
        });
    return res.data.success ? res.data.data : null;
  },
  /** Core services grouped from the location's Search Console queries, busiest first. */
  suggestions: async ({ locationId, practiceId }: SearchTermsScope): Promise<string[]> => {
    const res = practiceId
      ? await apiClient.get<ApiResponse<string[]>>(
          `${agencyPath(practiceId, locationId)}/suggestions`,
        )
      : await apiClient.get<ApiResponse<string[]>>('/seo/search-terms/suggestions', {
          params: { locationId },
        });
    return res.data.success ? res.data.data : [];
  },
  save: async (
    { locationId, practiceId }: SearchTermsScope,
    queries: string[],
  ): Promise<SearchTermSet> => {
    const res = practiceId
      ? await apiClient.post<ApiResponse<SearchTermSet>>(agencyPath(practiceId, locationId), {
          queries,
        })
      : await apiClient.post<ApiResponse<SearchTermSet>>('/seo/search-terms', {
          locationId,
          queries,
        });
    if (!res.data.success) throw new Error('failed to save search terms');
    return res.data.data;
  },
};
